/**
 * useLangSync — one-directional URL → i18n sync + i18n → <html> sync.
 *
 * Reads `?lang=<code>` on mount and on route change and applies it to
 * i18n. Also keeps <html lang|dir> aligned with the active language by
 * subscribing to i18next's `languageChanged` event directly (rather than
 * relying on React re-renders alone).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGS } from "@/lib/i18n";

const SUPPORTED = new Set(LANGS.map((l) => l.code));

function applyHtmlLang(lng) {
    const code = (lng || "en").split("-")[0];
    document.documentElement.lang = code;
    document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
}

export default function useLangSync() {
    const { i18n } = useTranslation();
    const { search } = useLocation();

    // URL → i18n. The URL is the single source of truth:
    //  - ?lang=<code>   → set i18n to that code (if supported)
    //  - no ?lang= param → treat as English (canonical)
    useEffect(() => {
        const params = new URLSearchParams(search);
        const q = params.get("lang");
        const target = q && SUPPORTED.has(q) ? q : "en";
        if (i18n.language?.split("-")[0] !== target) {
            i18n.changeLanguage(target);
        }
    }, [search, i18n]);

    // i18n → <html>. Re-apply on every route change too, so that any
    // transient DOM writes elsewhere cannot leave <html> stuck in a stale
    // state. Subscribe to i18next's languageChanged event as well.
    useEffect(() => {
        applyHtmlLang(i18n.language);
    }, [i18n, i18n.language, search]);

    useEffect(() => {
        const handler = (lng) => applyHtmlLang(lng);
        i18n.on("languageChanged", handler);
        return () => { i18n.off("languageChanged", handler); };
    }, [i18n]);
}
